import { supabase, callEdgeFunction } from './supabase';
import type {
  BlogPost,
  BlogCategory,
  BlogAuthor,
  BlogAuthorWithStats,
  BlogAuditLog,
  BlogStats,
  BlogStatus,
  BulkCreateBlogRequest,
  BulkCreateBlogResponse,
  GenerateBlogRequest,
  GenerateBlogResponse,
  DuplicatePost,
  DuplicateGroup,
  DuplicateDetectionResult,
  DuplicateDeletionRequest,
  DuplicateDeletionResponse,
  BulkDuplicateDeletionResponse,
  DuplicateMatchType,
  GenerateAuthorRequest,
  RefreshAuthorRequest,
  GenerateAuthorResponse,
  AuthorPostSummary,
  BulkReassignAuthorRequest,
  BulkReassignAuthorResponse,
} from '@/types/blog';
import { validateBlogSeoQa } from '@/utils/blogSeoQa';

// ============================================================================
// Blog Posts
// ============================================================================

export async function getBlogPosts(
  page = 1,
  pageSize = 20,
  status?: BlogStatus,
  categorySlug?: string,
  search?: string,
  batchId?: string
): Promise<{ posts: BlogPost[]; total: number }> {
  let query = supabase
    .from('blog_posts')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (status) {
    query = query.eq('status', status);
  }
  if (categorySlug) {
    query = query.eq('category_slug', categorySlug);
  }
  if (search) {
    query = query.or(`title.ilike.%${search}%,content.ilike.%${search}%`);
  }
  if (batchId) {
    query = query.eq('ai_generation_batch_id', batchId);
  }

  const { data, count, error } = await query;
  if (error) throw error;

  return { posts: (data || []) as BlogPost[], total: count || 0 };
}

export async function getAllBlogPostsForExport(): Promise<{
  title: string;
  category: string;
  status: string;
  slug: string;
  created_at: string;
}[]> {
  // Fetch all blog posts with category info for export
  // Using pagination to handle large datasets
  const allPosts: { title: string; category: string; status: string; slug: string; created_at: string }[] = [];
  const pageSize = 1000;
  let page = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('title, category_slug, status, slug, created_at')
      .order('created_at', { ascending: false })
      .range(page * pageSize, (page + 1) * pageSize - 1);

    if (error) throw error;

    if (data && data.length > 0) {
      // Map category_slug to category (business/consumer) based on parent
      const mappedPosts = data.map(post => ({
        title: post.title,
        category: post.category_slug || 'uncategorized',
        status: post.status,
        slug: post.slug,
        created_at: post.created_at,
      }));
      allPosts.push(...mappedPosts);

      if (data.length < pageSize) {
        hasMore = false;
      } else {
        page++;
      }
    } else {
      hasMore = false;
    }
  }

  return allPosts;
}

export async function getBlogPost(id: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(
      `
      *,
      category:blog_categories(id, name, slug),
      blog_author:blog_authors(id, name, bio, expertise, avatar_url)
    `
    )
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  return data as BlogPost;
}

export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(
      `
      *,
      category:blog_categories(id, name, slug),
      blog_author:blog_authors(id, name, bio, expertise, avatar_url)
    `
    )
    .eq('slug', slug)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as BlogPost;
}

export async function updateBlogPost(
  id: string,
  updates: Partial<BlogPost>
): Promise<BlogPost> {
  // Remove read-only fields
  const {
    id: _id,
    created_at,
    updated_at,
    word_count,
    reading_time_minutes,
    view_count,
    share_count,
    author,
    reviewer,
    category,
    blog_author,
    ...updateData
  } = updates;

  const { data, error } = await supabase
    .from('blog_posts')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as BlogPost;
}

export async function createBlogPost(
  post: Partial<BlogPost>
): Promise<BlogPost> {
  const { data, error } = await supabase
    .from('blog_posts')
    .insert(post)
    .select()
    .single();

  if (error) throw error;
  return data as BlogPost;
}

export async function deleteBlogPost(id: string): Promise<void> {
  const { error } = await supabase.from('blog_posts').delete().eq('id', id);

  if (error) throw error;
}

// ============================================================================
// Search Engine Indexing
// ============================================================================

const SITE_URL = 'https://linkedbysix.com';

/**
 * Submit URL to search engines for indexing (Google Indexing API + IndexNow)
 * Called automatically when posts are published/unpublished.
 * Non-blocking - failures don't prevent publication.
 */
async function submitToSearchEngines(
  url: string,
  action: 'URL_UPDATED' | 'URL_DELETED'
): Promise<void> {
  try {
    // Submit to both APIs in parallel, but don't block on failures
    const results = await Promise.allSettled([
      callEdgeFunction('google-indexing-api', { action, urls: [url] }).catch(err => {
        console.warn('Google Indexing API submission failed:', err);
        return { success: false, error: err.message };
      }),
      callEdgeFunction('indexnow-submit', { urls: [url] }).catch(err => {
        console.warn('IndexNow submission failed:', err);
        return { success: false, error: err.message };
      }),
    ]);

    // Log results for monitoring
    const googleResult = results[0];
    const indexNowResult = results[1];

    console.log('Search engine indexing results:', {
      url,
      action,
      google: googleResult.status === 'fulfilled' ? 'submitted' : 'failed',
      indexNow: indexNowResult.status === 'fulfilled' ? 'submitted' : 'failed',
    });
  } catch (error) {
    // Don't throw - indexing failures shouldn't block publishing
    console.error('Failed to submit to search engines:', error);
  }
}

// ============================================================================
// Publication Workflow
// ============================================================================

export async function submitForReview(
  id: string,
  notes?: string
): Promise<BlogPost> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session?.user) throw new Error('Not authenticated');

  return updateBlogPost(id, {
    status: 'in_review',
    submitted_for_review_at: new Date().toISOString(),
    review_notes: notes || null,
    rejection_reason: null, // Clear any previous rejection
  });
}

export async function approveBlogPost(id: string): Promise<BlogPost> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session?.user) throw new Error('Not authenticated');

  // Get the post to validate image requirements
  const post = await getBlogPost(id);
  if (!post) throw new Error('Post not found');

  const seoQa = validateBlogSeoQa(post);
  if (!seoQa.passes) {
    const failureSummary = seoQa.issues.map((issue) => issue.message).join(' ');
    throw new Error(`Cannot publish: SEO QA checklist failed. ${failureSummary}`);
  }

  // Validate featured image and alt text unless override is enabled
  const hasImage = !!post.featured_image_url?.trim();
  const hasAlt = !!post.featured_image_alt?.trim();

  if (!hasImage && !post.allow_publish_without_image) {
    throw new Error('Cannot publish: Featured image is required. Generate or upload an image, or enable "Publish without image" override.');
  }

  if (hasImage && !hasAlt && !post.allow_publish_without_image) {
    throw new Error('Cannot publish: Alt text is required for the featured image. Add alt text or enable "Publish without image" override.');
  }

  // Get admin user ID
  const { data: adminUser } = await supabase
    .from('admin_users')
    .select('id')
    .eq('user_id', session.session.user.id)
    .single();

  // If publishing without image (with override), log to audit trail
  if (post.allow_publish_without_image && (!hasImage || !hasAlt)) {
    await supabase.from('blog_audit_log').insert({
      blog_post_id: id,
      admin_user_id: adminUser?.id || null,
      action: 'publish_without_image',
      notes: `Post published without ${!hasImage ? 'featured image' : 'alt text'} (override enabled)`,
      new_values: {
        allow_publish_without_image: true,
        has_image: hasImage,
        has_alt: hasAlt
      },
    });
  }

  const result = await updateBlogPost(id, {
    status: 'published',
    published_at: new Date().toISOString(),
    reviewer_id: adminUser?.id || null,
    rejection_reason: null,
  });

  // Submit to search engines for indexing (non-blocking)
  const postUrl = `${SITE_URL}/blog/${result.slug}`;
  submitToSearchEngines(postUrl, 'URL_UPDATED');

  // Also index the category page when a post is published to it
  if (result.category_slug) {
    // Canonical category pages are path-based (matches sitemap + SSR hub pages)
    const categoryUrl = `${SITE_URL}/blog/category/${result.category_slug}`;
    submitToSearchEngines(categoryUrl, 'URL_UPDATED');
  }

  // Notify search engines to re-crawl the archive page so it picks up the new article
  submitToSearchEngines(`${SITE_URL}/blog/archive`, 'URL_UPDATED');

  return result;
}

/**
 * Set or clear the "allow publish without image" override for a blog post.
 * This should only be used as a last resort when an image cannot be generated.
 */
export async function setAllowPublishWithoutImage(
  id: string,
  allow: boolean
): Promise<BlogPost> {
  return updateBlogPost(id, { allow_publish_without_image: allow });
}

export async function rejectBlogPost(
  id: string,
  reason: string
): Promise<BlogPost> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session?.user) throw new Error('Not authenticated');

  // Get admin user ID
  const { data: adminUser } = await supabase
    .from('admin_users')
    .select('id')
    .eq('user_id', session.session.user.id)
    .single();

  return updateBlogPost(id, {
    status: 'draft',
    rejection_reason: reason,
    reviewer_id: adminUser?.id || null,
  });
}

export async function archiveBlogPost(id: string): Promise<BlogPost> {
  return updateBlogPost(id, { status: 'archived' });
}

export async function unpublishBlogPost(id: string): Promise<BlogPost> {
  // Get the post first to get the slug for deindexing
  const post = await getBlogPost(id);

  const result = await updateBlogPost(id, {
    status: 'draft',
    published_at: null,
  });

  // Notify search engines to remove from index (non-blocking)
  if (post?.slug) {
    const postUrl = `${SITE_URL}/blog/${post.slug}`;
    submitToSearchEngines(postUrl, 'URL_DELETED');
  }

  return result;
}

// ============================================================================
// Bulk Operations
// ============================================================================

export async function bulkCreateBlogPosts(
  request: BulkCreateBlogRequest
): Promise<BulkCreateBlogResponse> {
  return callEdgeFunction<BulkCreateBlogResponse>(
    'admin-bulk-create-blog-posts',
    request
  );
}

export async function generateBlogPosts(
  request: GenerateBlogRequest
): Promise<GenerateBlogResponse> {
  return callEdgeFunction<GenerateBlogResponse>(
    'ai-generate-blog-content',
    request
  );
}

/**
 * Generate a single blog post (one topic at a time to avoid timeout)
 * This is the preferred method for blog generation as it fits within
 * the edge function timeout limit.
 *
 * Uses two-phase generation:
 * - Phase 1 (content): Generate blog content with Claude (~30-60s)
 * - Phase 2 (image): Generate featured image with Flux 1.1 Pro (~10-30s)
 *
 * Each phase fits within the 150s edge function timeout.
 * Includes automatic retry with exponential backoff for transient errors.
 */
export async function generateSingleBlogPost(
  topic: GenerateBlogRequest['topics'][0],
  jobId: string,
  topicIndex: number,
  options?: GenerateBlogRequest['options'],
  signal?: AbortSignal
): Promise<GenerateBlogResponse> {
  // Phase 1: Generate content only (no image)
  // Retry up to 2 times with exponential backoff for server errors
  return callEdgeFunction<GenerateBlogResponse>(
    'ai-generate-blog-content',
    {
      topics: [topic],
      job_id: jobId,
      single_topic: true,
      topic_index: topicIndex,
      phase: 'content', // Content-only mode - skip image generation
      options,
    },
    {
      signal,
      maxRetries: 2,
      initialDelayMs: 2000,
      maxDelayMs: 15000,
      retryOnTimeout: false, // Don't retry timeouts for content (may have partially saved)
    }
  );
}

/**
 * Response from image generation phase
 */
export interface GenerateImageResponse {
  success: boolean;
  blog_id: string;
  image_url?: string;
  image_alt?: string;
  message?: string;
}

/**
 * Generate featured image for a blog post (Phase 2 of two-phase generation)
 * This is called after content generation succeeds, for posts that need images.
 * Includes automatic retry with exponential backoff.
 */
export async function generateBlogImage(
  blogId: string,
  imagePrompt: string,
  imageAlt: string,
  signal?: AbortSignal
): Promise<GenerateImageResponse> {
  return callEdgeFunction<GenerateImageResponse>(
    'ai-generate-blog-content',
    {
      phase: 'image',
      blog_id: blogId,
      image_prompt: imagePrompt,
      image_alt: imageAlt,
    },
    {
      signal,
      maxRetries: 2,
      initialDelayMs: 3000,
      maxDelayMs: 20000,
      retryOnTimeout: true, // Safe to retry image generation
    }
  );
}

/**
 * Regenerate featured image for a blog post (for use in preview/edit mode)
 * Returns the new image URL without saving - caller decides whether to keep it.
 */
export async function regenerateBlogImage(
  blogId: string,
  imagePrompt: string,
  imageAlt: string
): Promise<GenerateImageResponse> {
  return callEdgeFunction<GenerateImageResponse>(
    'ai-generate-blog-content',
    {
      phase: 'image',
      blog_id: blogId,
      image_prompt: imagePrompt,
      image_alt: imageAlt,
    }
  );
}

/**
 * Get AI-generated posts that are missing images but have an image prompt saved.
 * These are posts where image generation failed (e.g., due to API credits running out).
 */
export async function getPostsMissingImages(): Promise<{ id: string; title: string; featured_image_suggestion: string }[]> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id, title, featured_image_suggestion')
    .is('featured_image_url', null)
    .not('featured_image_suggestion', 'is', null)
    .eq('ai_generated', true);

  if (error) throw error;
  return data || [];
}

// ============================================================================
// Blog Generation Job Tracking
// ============================================================================

export interface BlogGenerationJob {
  id: string;
  created_at: string;
  updated_at: string;
  topics: Array<{
    topic: string;
    category: 'consumer' | 'business';
    subcategory?: string;
  }>;
  total_count: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  current_index: number;
  current_topic: string | null;
  results: GenerateBlogResponse['results'];
  success_count: number;
  error_count: number;
  error_message: string | null;
  created_by: string | null;
}

/**
 * Get the current status of a blog generation job
 */
export async function getBlogGenerationJob(jobId: string): Promise<BlogGenerationJob | null> {
  const { data, error } = await supabase
    .from('blog_generation_jobs')
    .select('*')
    .eq('id', jobId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as BlogGenerationJob;
}

/**
 * Create a blog generation job (client-side) and return its ID
 * This allows us to start showing progress immediately before calling the edge function
 */
export async function createBlogGenerationJob(
  topics: GenerateBlogRequest['topics']
): Promise<string> {
  const { data: session } = await supabase.auth.getSession();

  const { data, error } = await supabase
    .from('blog_generation_jobs')
    .insert({
      topics: topics,
      total_count: topics.length,
      status: 'pending',
      current_index: 0,
      current_topic: null,
      results: [],
      success_count: 0,
      error_count: 0,
      created_by: session?.session?.user?.id || null,
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/**
 * Get the most recent blog generation job (for resuming after page refresh)
 */
export async function getLatestBlogGenerationJob(): Promise<BlogGenerationJob | null> {
  const { data, error } = await supabase
    .from('blog_generation_jobs')
    .select('*')
    .in('status', ['pending', 'running'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data as BlogGenerationJob | null;
}

/**
 * Cancel a running blog generation job
 */
export async function cancelBlogGenerationJob(jobId: string): Promise<void> {
  const { error } = await supabase
    .from('blog_generation_jobs')
    .update({ status: 'cancelled', updated_at: new Date().toISOString() })
    .eq('id', jobId);

  if (error) throw error;
}

/**
 * Update job progress from client side (before calling edge function for single topic)
 */
export async function updateBlogGenerationJobProgress(
  jobId: string,
  currentIndex: number,
  currentTopic: string | null
): Promise<void> {
  const { error } = await supabase
    .from('blog_generation_jobs')
    .update({
      status: 'running',
      current_index: currentIndex,
      current_topic: currentTopic,
      updated_at: new Date().toISOString(),
    })
    .eq('id', jobId);

  if (error) throw error;
}

/**
 * Mark job as completed from client side
 */
export async function completeBlogGenerationJob(
  jobId: string,
  successCount: number,
  errorCount: number
): Promise<void> {
  const { error } = await supabase
    .from('blog_generation_jobs')
    .update({
      status: 'completed',
      success_count: successCount,
      error_count: errorCount,
      current_topic: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', jobId);

  if (error) throw error;
}

/**
 * Resume a previously started blog generation job (legacy - uses client-side processing)
 * @deprecated Use startBlogQueueProcessing instead for server-side processing
 */
export async function resumeBlogGenerationJob(jobId: string): Promise<GenerateBlogResponse> {
  return callEdgeFunction<GenerateBlogResponse>(
    'ai-generate-blog-content',
    { topics: [], job_id: jobId }
  );
}

/**
 * Start server-side queue processing for a blog generation job.
 * The server will process all topics automatically, even if the browser is closed.
 */
export async function startBlogQueueProcessing(jobId: string): Promise<void> {
  await callEdgeFunction<{ success: boolean; message: string }>('process-blog-queue', { job_id: jobId });
}

/**
 * Retry failed topics from a completed job by creating a new job with just the failed topics.
 * Returns the new job ID.
 */
export async function retryFailedTopics(jobId: string): Promise<string> {
  const job = await getBlogGenerationJob(jobId);
  if (!job) throw new Error('Job not found');

  const failedTopics = job.results
    .filter(r => !r.success)
    .map(r => job.topics.find(t => t.topic === r.topic))
    .filter((t): t is NonNullable<typeof t> => t !== undefined);

  if (failedTopics.length === 0) throw new Error('No failed topics to retry');

  const newJobId = await createBlogGenerationJob(failedTopics);
  await startBlogQueueProcessing(newJobId);
  return newJobId;
}

/**
 * Check if a job is stalled (running but no heartbeat in 5 minutes)
 */
export function isJobStalled(job: BlogGenerationJob): boolean {
  if (job.status !== 'running') return false;

  // Check last_heartbeat if available (new column)
  const lastHeartbeat = (job as { last_heartbeat?: string }).last_heartbeat;
  if (lastHeartbeat) {
    const heartbeatTime = new Date(lastHeartbeat).getTime();
    const stalledThreshold = 5 * 60 * 1000; // 5 minutes
    return Date.now() - heartbeatTime > stalledThreshold;
  }

  // Fallback to updated_at
  const updatedAt = new Date(job.updated_at).getTime();
  const stalledThreshold = 5 * 60 * 1000; // 5 minutes
  return Date.now() - updatedAt > stalledThreshold;
}

export async function bulkUpdateStatus(
  ids: string[],
  status: BlogStatus
): Promise<void> {
  const updates: Partial<BlogPost> = { status };

  if (status === 'published') {
    updates.published_at = new Date().toISOString();
  }

  const { error } = await supabase
    .from('blog_posts')
    .update(updates)
    .in('id', ids);

  if (error) throw error;
}

export async function bulkDelete(ids: string[]): Promise<void> {
  const { error } = await supabase.from('blog_posts').delete().in('id', ids);

  if (error) throw error;
}

// ============================================================================
// Categories
// ============================================================================

export async function getBlogCategories(): Promise<BlogCategory[]> {
  const { data, error } = await supabase
    .from('blog_categories')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return (data || []) as BlogCategory[];
}

export async function getAllBlogCategories(): Promise<BlogCategory[]> {
  const { data, error } = await supabase
    .from('blog_categories')
    .select('*')
    .order('sort_order');

  if (error) throw error;
  return (data || []) as BlogCategory[];
}

export async function createBlogCategory(
  category: Partial<BlogCategory>
): Promise<BlogCategory> {
  const { data, error } = await supabase
    .from('blog_categories')
    .insert(category)
    .select()
    .single();

  if (error) throw error;
  return data as BlogCategory;
}

export async function updateBlogCategory(
  id: string,
  updates: Partial<BlogCategory>
): Promise<BlogCategory> {
  const { data, error } = await supabase
    .from('blog_categories')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as BlogCategory;
}

// ============================================================================
// Stats
// ============================================================================

export async function getBlogStats(): Promise<BlogStats> {
  const { data, error } = await supabase.rpc('get_blog_stats');

  if (error) throw error;

  // The RPC returns a single row
  const stats = Array.isArray(data) ? data[0] : data;

  return {
    total_posts: stats?.total_posts || 0,
    drafts: stats?.drafts || 0,
    in_review: stats?.in_review || 0,
    published: stats?.published || 0,
    scheduled: stats?.scheduled || 0,
    live: stats?.live || 0,
    archived: stats?.archived || 0,
    ai_generated: stats?.ai_generated || 0,
    total_views: stats?.total_views || 0,
  };
}

// ============================================================================
// Audit Log
// ============================================================================

export async function getBlogAuditLog(
  postId: string,
  limit = 50
): Promise<BlogAuditLog[]> {
  const { data, error } = await supabase
    .from('blog_audit_log')
    .select('*')
    .eq('blog_post_id', postId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []) as BlogAuditLog[];
}

// ============================================================================
// Search
// ============================================================================

export async function searchBlogPosts(
  query: string,
  limit = 10
): Promise<BlogPost[]> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id, title, slug, status, category_slug, created_at')
    .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []) as BlogPost[];
}

// ============================================================================
// Blog Authors
// ============================================================================

export async function getBlogAuthors(): Promise<BlogAuthor[]> {
  const { data, error } = await supabase
    .from('blog_authors')
    .select('*')
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  return (data || []) as BlogAuthor[];
}

export async function getAllBlogAuthors(): Promise<BlogAuthor[]> {
  const { data, error } = await supabase
    .from('blog_authors')
    .select('*')
    .order('name');

  if (error) throw error;
  return (data || []) as BlogAuthor[];
}

export async function getBlogAuthor(id: string): Promise<BlogAuthor | null> {
  const { data, error } = await supabase
    .from('blog_authors')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as BlogAuthor;
}

export async function createBlogAuthor(
  author: Partial<BlogAuthor>
): Promise<BlogAuthor> {
  const { data, error } = await supabase
    .from('blog_authors')
    .insert(author)
    .select()
    .single();

  if (error) throw error;
  return data as BlogAuthor;
}

export async function updateBlogAuthor(
  id: string,
  updates: Partial<BlogAuthor>
): Promise<BlogAuthor> {
  const { data, error } = await supabase
    .from('blog_authors')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as BlogAuthor;
}

export async function deleteBlogAuthor(id: string): Promise<void> {
  const { error } = await supabase.from('blog_authors').delete().eq('id', id);
  if (error) throw error;
}

// ============================================================================
// Blog Authors - Extended API
// ============================================================================

/**
 * Get all blog authors with their statistics (post counts, views, etc.)
 */
export async function getBlogAuthorsWithStats(
  page = 1,
  pageSize = 20,
  search?: string,
  isActive?: boolean
): Promise<{ authors: BlogAuthorWithStats[]; total: number }> {
  // First get the authors
  let query = supabase
    .from('blog_authors')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (search) {
    query = query.or(`name.ilike.%${search}%,bio.ilike.%${search}%`);
  }
  if (isActive !== undefined) {
    query = query.eq('is_active', isActive);
  }

  const { data: authors, count, error } = await query;
  if (error) throw error;

  if (!authors || authors.length === 0) {
    return { authors: [], total: count || 0 };
  }

  // Get stats for each author
  const authorIds = authors.map(a => a.id);
  const { data: statsData, error: statsError } = await supabase
    .from('blog_posts')
    .select('blog_author_id, status, view_count, share_count')
    .in('blog_author_id', authorIds);

  if (statsError) {
    console.error('Failed to fetch author stats:', statsError);
  }

  // Calculate stats per author
  const statsMap = new Map<string, {
    total_posts: number;
    published_posts: number;
    draft_posts: number;
    total_views: number;
    total_shares: number;
  }>();

  authorIds.forEach(id => {
    statsMap.set(id, {
      total_posts: 0,
      published_posts: 0,
      draft_posts: 0,
      total_views: 0,
      total_shares: 0,
    });
  });

  (statsData || []).forEach(post => {
    const stats = statsMap.get(post.blog_author_id);
    if (stats) {
      stats.total_posts++;
      if (post.status === 'published') stats.published_posts++;
      if (post.status === 'draft') stats.draft_posts++;
      stats.total_views += post.view_count || 0;
      stats.total_shares += post.share_count || 0;
    }
  });

  // Combine authors with stats
  const authorsWithStats: BlogAuthorWithStats[] = authors.map(author => ({
    ...author,
    ...statsMap.get(author.id)!,
  }));

  return { authors: authorsWithStats, total: count || 0 };
}

/**
 * Get a single author with their statistics
 */
export async function getBlogAuthorWithStats(id: string): Promise<BlogAuthorWithStats | null> {
  const { data, error } = await supabase
    .rpc('get_blog_author_with_stats', { author_id_param: id });

  if (error) {
    // If RPC doesn't exist yet, fall back to manual query
    if (error.code === '42883') {
      const author = await getBlogAuthor(id);
      if (!author) return null;

      // Get stats manually
      const { data: posts } = await supabase
        .from('blog_posts')
        .select('status, view_count, share_count')
        .eq('blog_author_id', id);

      const stats = {
        total_posts: posts?.length || 0,
        published_posts: posts?.filter(p => p.status === 'published').length || 0,
        draft_posts: posts?.filter(p => p.status === 'draft').length || 0,
        total_views: posts?.reduce((sum, p) => sum + (p.view_count || 0), 0) || 0,
        total_shares: posts?.reduce((sum, p) => sum + (p.share_count || 0), 0) || 0,
      };

      return { ...author, ...stats };
    }
    throw error;
  }

  if (!data || data.length === 0) return null;
  return data[0] as BlogAuthorWithStats;
}

/**
 * Get posts by a specific author
 */
export async function getPostsByAuthor(
  authorId: string,
  page = 1,
  pageSize = 20
): Promise<{ posts: AuthorPostSummary[]; total: number }> {
  const { data, count, error } = await supabase
    .from('blog_posts')
    .select('id, title, slug, status, category_slug, created_at, view_count', { count: 'exact' })
    .eq('blog_author_id', authorId)
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (error) throw error;
  return { posts: (data || []) as AuthorPostSummary[], total: count || 0 };
}

/**
 * Generate a new blog author using AI
 */
export async function generateBlogAuthor(
  request: GenerateAuthorRequest
): Promise<GenerateAuthorResponse> {
  return callEdgeFunction<GenerateAuthorResponse>('ai-generate-author', request);
}

/**
 * Refresh/regenerate an existing author's name and bio using AI
 */
export async function refreshBlogAuthor(
  request: RefreshAuthorRequest
): Promise<GenerateAuthorResponse> {
  return callEdgeFunction<GenerateAuthorResponse>('ai-generate-author', request);
}

/**
 * Bulk reassign blog posts to a different author
 */
export async function bulkReassignAuthor(
  postIds: string[],
  newAuthorId: string
): Promise<BulkReassignAuthorResponse> {
  // Try using RPC first
  const { data, error } = await supabase
    .rpc('bulk_reassign_blog_author', {
      post_ids: postIds,
      new_author_id: newAuthorId,
    });

  if (error) {
    // If RPC doesn't exist, fall back to direct update
    if (error.code === '42883') {
      const { error: updateError } = await supabase
        .from('blog_posts')
        .update({ blog_author_id: newAuthorId, updated_at: new Date().toISOString() })
        .in('id', postIds);

      if (updateError) {
        return { success: false, updated_count: 0, error: updateError.message };
      }
      return { success: true, updated_count: postIds.length };
    }
    return { success: false, updated_count: 0, error: error.message };
  }

  const result = data?.[0] || { updated_count: 0, success: true };
  return { success: result.success, updated_count: result.updated_count };
}

/**
 * Toggle author active status
 */
export async function toggleBlogAuthorActive(
  id: string,
  isActive: boolean
): Promise<BlogAuthor> {
  const { data, error } = await supabase
    .from('blog_authors')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as BlogAuthor;
}

/**
 * Get posts that have no author assigned
 */
export async function getPostsWithoutAuthor(
  page = 1,
  pageSize = 20
): Promise<{ posts: AuthorPostSummary[]; total: number }> {
  const { data, count, error } = await supabase
    .from('blog_posts')
    .select('id, title, slug, status, category_slug, created_at, view_count', { count: 'exact' })
    .is('blog_author_id', null)
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (error) throw error;
  return { posts: (data || []) as AuthorPostSummary[], total: count || 0 };
}

// ============================================================================
// Automated Author Reassignment
// ============================================================================

export interface AuthorReassignmentAnalysis {
  post_id: string;
  title: string;
  slug: string;
  category_slug: string;
  subcategory: string | null;
  current_author_id: string | null;
  current_author_name: string | null;
  recommended_author_id: string;
  recommended_author_name: string;
  match_score: number;
  match_reason: string;
  will_change: boolean;
}

export interface AnalyzeReassignmentResponse {
  success: boolean;
  mode: 'analyze';
  total_posts: number;
  posts_without_author: number;
  posts_with_mismatch: number;
  recommendations: AuthorReassignmentAnalysis[];
}

export interface ReassignAuthorsResponse {
  success: boolean;
  mode: 'reassign';
  dry_run: boolean;
  total_processed: number;
  posts_updated: number;
  posts_skipped: number;
  errors: Array<{ post_id: string; error: string }>;
}

/**
 * Analyze blog posts to find optimal author assignments
 */
export async function analyzeAuthorReassignments(
  unassignedOnly = false,
  categorySlug?: string
): Promise<AnalyzeReassignmentResponse> {
  return callEdgeFunction<AnalyzeReassignmentResponse>('reassign-blog-authors', {
    mode: 'analyze',
    unassigned_only: unassignedOnly,
    category_slug: categorySlug,
  });
}

/**
 * Execute author reassignments based on best match logic
 */
export async function executeAuthorReassignments(
  unassignedOnly = false,
  dryRun = false,
  categorySlug?: string
): Promise<ReassignAuthorsResponse> {
  return callEdgeFunction<ReassignAuthorsResponse>('reassign-blog-authors', {
    mode: 'reassign',
    unassigned_only: unassignedOnly,
    dry_run: dryRun,
    category_slug: categorySlug,
  });
}

// ============================================================================
// Topic Coverage Analysis
// ============================================================================

export interface ExistingTopic {
  id: string;
  title: string;
  primary_keyword: string | null;
  category_slug: string;
  subcategory: string | null;
  status: BlogStatus;
}

/**
 * Fetches all existing blog post topics for gap analysis.
 * Returns minimal data needed to analyze content coverage.
 */
export async function getExistingTopics(): Promise<ExistingTopic[]> {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id, title, primary_keyword, category_slug, subcategory, status')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as ExistingTopic[];
}

// ============================================================================
// Content Remediation (Fix problematic "ask friends" messaging)
// ============================================================================

export interface ContentIssue {
  type: 'manual_outreach_cta' | 'ask_network_content' | 'problematic_answer_capsule';
  location: string;
  original_text: string;
  description: string;
}

export interface BlogAnalysis {
  blog_id: string;
  slug: string;
  title: string;
  has_issues: boolean;
  issues: ContentIssue[];
}

export interface AnalyzeContentResponse {
  success: boolean;
  total_ai_blogs: number;
  analyzed: number;
  blogs_with_issues: number;
  analyses: BlogAnalysis[];
}

export interface FixResult {
  blog_id: string;
  slug: string;
  title: string;
  success: boolean;
  changes_made: number;
  original_content?: string;
  fixed_content?: string;
  error?: string;
}

export interface FixContentResponse {
  success: boolean;
  mode: 'preview' | 'fix';
  dry_run: boolean;
  processed: number;
  fixed: number;
  unchanged: number;
  failed: number;
  results: FixResult[];
}

/**
 * Analyze AI-generated blog posts for problematic content patterns
 * (e.g., "ask friends for recommendations" which contradicts Linked By Six's value)
 */
export async function analyzeContentIssues(
  limit = 100,
  offset = 0
): Promise<AnalyzeContentResponse> {
  return callEdgeFunction<AnalyzeContentResponse>('fix-blog-content', {
    mode: 'analyze',
    limit,
    offset,
  });
}

/**
 * Preview content fixes without saving (shows before/after)
 */
export async function previewContentFixes(
  blogIds: string[]
): Promise<FixContentResponse> {
  return callEdgeFunction<FixContentResponse>('fix-blog-content', {
    mode: 'preview',
    blog_ids: blogIds,
  });
}

/**
 * Apply content fixes to specific blog posts
 */
export async function fixBlogContent(
  blogIds: string[],
  dryRun = false
): Promise<FixContentResponse> {
  return callEdgeFunction<FixContentResponse>('fix-blog-content', {
    mode: 'fix',
    blog_ids: blogIds,
    dry_run: dryRun,
  });
}

/**
 * Fix all AI-generated blogs with issues (batch operation)
 */
export async function fixAllBlogContent(
  limit = 10,
  offset = 0,
  dryRun = false
): Promise<FixContentResponse> {
  return callEdgeFunction<FixContentResponse>('fix-blog-content', {
    mode: 'fix',
    limit,
    offset,
    dry_run: dryRun,
  });
}

// ============================================================================
// Duplicate Detection & Management
// ============================================================================

/**
 * Apply deletion logic to determine which post to keep from a group:
 * 1. Has featured_image_url (keep) vs no image (delete)
 * 2. If tie on images: newer created_at wins (keep newer, delete older)
 */
function applyDeletionLogic(posts: DuplicatePost[]): { keepId: string; deleteIds: string[] } {
  // Sort by: has image DESC, created_at DESC
  const sorted = [...posts].sort((a, b) => {
    const aHasImage = a.featured_image_url ? 1 : 0;
    const bHasImage = b.featured_image_url ? 1 : 0;

    if (bHasImage !== aHasImage) {
      return bHasImage - aHasImage; // Image first
    }

    // Same image status: newer first
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  return {
    keepId: sorted[0].id,
    deleteIds: sorted.slice(1).map((p) => p.id),
  };
}

/**
 * Calculate word similarity between two titles
 * Returns a value between 0 and 1
 */
function calculateTitleSimilarity(title1: string, title2: string): number {
  const words1 = title1
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 3);
  const words2 = title2
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 3);

  if (words1.length === 0 || words2.length === 0) return 0;

  const commonWords = words1.filter((w) => words2.includes(w));
  return commonWords.length / Math.max(words1.length, words2.length);
}

/**
 * Find posts with similar titles (>70% word overlap)
 * Only includes groups not already found by primary keyword matching
 */
function findSimilarTitles(
  posts: DuplicatePost[],
  threshold: number,
  existingGroupIds: Set<string>
): DuplicateGroup[] {
  const groups: DuplicateGroup[] = [];
  const processed = new Set<string>();

  for (let i = 0; i < posts.length; i++) {
    // Skip posts already in a primary keyword group
    if (processed.has(posts[i].id) || existingGroupIds.has(posts[i].id)) continue;

    const similarPosts = [posts[i]];

    for (let j = i + 1; j < posts.length; j++) {
      if (processed.has(posts[j].id) || existingGroupIds.has(posts[j].id)) continue;

      const similarity = calculateTitleSimilarity(posts[i].title, posts[j].title);

      if (similarity >= threshold) {
        similarPosts.push(posts[j]);
        processed.add(posts[j].id);
      }
    }

    if (similarPosts.length > 1) {
      processed.add(posts[i].id);
      const { keepId, deleteIds } = applyDeletionLogic(similarPosts);

      groups.push({
        group_key: `title_${posts[i].id}`,
        match_type: 'similar_title',
        posts: similarPosts,
        recommended_keep_id: keepId,
        recommended_delete_ids: deleteIds,
      });
    }
  }

  return groups;
}

/**
 * Find posts with same category + subcategory + 3+ overlapping secondary keywords
 * Only includes groups not already found by other criteria
 */
function findCategoryKeywordOverlaps(
  posts: DuplicatePost[],
  existingGroupIds: Set<string>
): DuplicateGroup[] {
  const groups: DuplicateGroup[] = [];
  const processed = new Set<string>();

  // Group posts by category + subcategory
  const categoryGroups = new Map<string, DuplicatePost[]>();
  for (const post of posts) {
    if (existingGroupIds.has(post.id)) continue;
    const key = `${post.category_slug}|${post.subcategory || ''}`;
    if (!categoryGroups.has(key)) {
      categoryGroups.set(key, []);
    }
    categoryGroups.get(key)!.push(post);
  }

  // Within each category group, find posts with 3+ overlapping keywords
  for (const [categoryKey, categoryPosts] of categoryGroups) {
    if (categoryPosts.length < 2) continue;

    for (let i = 0; i < categoryPosts.length; i++) {
      if (processed.has(categoryPosts[i].id)) continue;

      const post1Keywords = (categoryPosts[i].meta_keywords || []).map((k) =>
        k.toLowerCase().trim()
      );
      if (post1Keywords.length < 3) continue;

      const overlappingPosts = [categoryPosts[i]];

      for (let j = i + 1; j < categoryPosts.length; j++) {
        if (processed.has(categoryPosts[j].id)) continue;

        const post2Keywords = (categoryPosts[j].meta_keywords || []).map((k) =>
          k.toLowerCase().trim()
        );
        const sharedKeywords = post1Keywords.filter((k) => post2Keywords.includes(k));

        if (sharedKeywords.length >= 3) {
          overlappingPosts.push(categoryPosts[j]);
          processed.add(categoryPosts[j].id);
        }
      }

      if (overlappingPosts.length > 1) {
        processed.add(categoryPosts[i].id);
        const { keepId, deleteIds } = applyDeletionLogic(overlappingPosts);

        groups.push({
          group_key: `category_${categoryKey}_${categoryPosts[i].id}`,
          match_type: 'category_keywords',
          posts: overlappingPosts,
          recommended_keep_id: keepId,
          recommended_delete_ids: deleteIds,
        });
      }
    }
  }

  return groups;
}

/**
 * Fetches all duplicate groups from the database.
 * Groups posts by: exact primary keyword, similar titles, category+keywords overlap.
 */
export async function getDuplicateGroups(): Promise<DuplicateDetectionResult> {
  // Step 1: Fetch all posts with relevant fields (exclude archived)
  const { data: posts, error } = await supabase
    .from('blog_posts')
    .select(
      'id, title, slug, primary_keyword, category_slug, subcategory, meta_keywords, featured_image_url, status, created_at, view_count'
    )
    .neq('status', 'archived')
    .order('created_at', { ascending: false });

  if (error) throw error;

  const allPosts = (posts || []) as DuplicatePost[];
  const groups: DuplicateGroup[] = [];
  const idsInGroups = new Set<string>();

  // Step 2: Group by exact primary keyword match
  const keywordGroups = new Map<string, DuplicatePost[]>();

  for (const post of allPosts) {
    if (post.primary_keyword) {
      const key = post.primary_keyword.toLowerCase().trim();
      if (!keywordGroups.has(key)) {
        keywordGroups.set(key, []);
      }
      keywordGroups.get(key)!.push(post);
    }
  }

  // Build duplicate groups for primary keyword matches
  for (const [key, groupPosts] of keywordGroups) {
    if (groupPosts.length > 1) {
      const { keepId, deleteIds } = applyDeletionLogic(groupPosts);

      groups.push({
        group_key: key,
        match_type: 'primary_keyword',
        posts: groupPosts,
        recommended_keep_id: keepId,
        recommended_delete_ids: deleteIds,
      });

      // Track which posts are already in a group
      groupPosts.forEach((p) => idsInGroups.add(p.id));
    }
  }

  // Step 3: Find similar titles (>70% word overlap)
  // Only for posts not already in a primary keyword group
  const titleGroups = findSimilarTitles(allPosts, 0.7, idsInGroups);
  groups.push(...titleGroups);
  titleGroups.forEach((g) => g.posts.forEach((p) => idsInGroups.add(p.id)));

  // Step 4: Find category+subcategory+keyword overlaps
  // Only for posts not already in other groups
  const categoryGroups = findCategoryKeywordOverlaps(allPosts, idsInGroups);
  groups.push(...categoryGroups);

  // Calculate totals
  const totalDuplicates = groups.reduce((sum, g) => sum + g.posts.length - 1, 0);

  return {
    total_groups: groups.length,
    total_duplicates: totalDuplicates,
    groups,
  };
}

/**
 * Delete duplicate posts, keeping the recommended one
 */
export async function deleteDuplicates(
  request: DuplicateDeletionRequest
): Promise<DuplicateDeletionResponse> {
  try {
    // Get info about posts before deletion
    const { data: postsToDelete, error: fetchError } = await supabase
      .from('blog_posts')
      .select('id, title, slug')
      .in('id', request.delete_ids);

    if (fetchError) throw fetchError;

    const { data: keptPost, error: keptError } = await supabase
      .from('blog_posts')
      .select('id, title, slug')
      .eq('id', request.keep_id)
      .single();

    if (keptError) throw keptError;

    // Perform deletion
    const { error: deleteError } = await supabase
      .from('blog_posts')
      .delete()
      .in('id', request.delete_ids);

    if (deleteError) throw deleteError;

    return {
      success: true,
      deleted_count: postsToDelete?.length || 0,
      kept_post: keptPost,
      deleted_posts: postsToDelete || [],
    };
  } catch (err) {
    return {
      success: false,
      deleted_count: 0,
      kept_post: { id: request.keep_id, title: '', slug: '' },
      deleted_posts: [],
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Bulk delete multiple duplicate groups at once
 */
export async function bulkDeleteDuplicates(
  requests: DuplicateDeletionRequest[]
): Promise<BulkDuplicateDeletionResponse> {
  const results: DuplicateDeletionResponse[] = [];
  let totalDeleted = 0;

  for (const request of requests) {
    const result = await deleteDuplicates(request);
    results.push(result);
    if (result.success) {
      totalDeleted += result.deleted_count;
    }
  }

  return {
    success: results.every((r) => r.success),
    total_deleted: totalDeleted,
    results,
  };
}
